import { Tabs } from '@design-systems/canvas-ui'
import { PageTabs } from 'components/page-tabs'
import { MPMLayout } from 'components/layout'
import { ErrorBoundary } from 'react-error-boundary'
import { ErrorFallback } from 'components/error-fallback'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { isStore } from 'utils/location'
import { useEssentials } from 'hooks/utils/useEssentials'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useMemo } from 'react'
import { useLogClick } from 'utils/analytics'
import { getPayrollPaths } from './helpers'

export const PayrollPage = () => {
    const { currentLocation } = useEssentials()
    const navigate = useNavigate()
    const locationFromRouter = useLocation()

    const searchParams = useMemo(
        () => new URLSearchParams(locationFromRouter.search),
        [locationFromRouter.search],
    )

    useEffect(() => {
        const { expectedPath, fullPath, validPaths } = getPayrollPaths(
            currentLocation,
            searchParams,
        )
        if (
            validPaths.includes(locationFromRouter.pathname) &&
            locationFromRouter.pathname !== expectedPath
        ) {
            void navigate(fullPath, { replace: true })
        }
    }, [currentLocation, locationFromRouter.pathname, navigate, searchParams])

    return <Payroll key={currentLocation} />
}

const Payroll = () => {
    const { t, currentLocation } = useEssentials()
    const { payroll } = TRANSLATION_KEYS
    const { logClick } = useLogClick('payroll')
    const navigate = useNavigate()
    const locationFromRouter = useLocation()

    const tabs = useMemo(() => {
        const commonTabs = ['workgroup', 'overtime']
        return isStore(currentLocation)
            ? ['monthly', ...commonTabs]
            : ['location', ...commonTabs]
    }, [currentLocation])

    const tabToPath = useMemo(
        () => ({
            location: 'location',
            workgroup: 'workgroup',
            overtime: 'overtime',
            monthly: 'monthly',
        }),
        [],
    )

    const tabTranslations = useMemo(
        () => ({
            location: t(payroll.tabs.location),
            workgroup: t(payroll.tabs.workgroup),
            overtime: t(payroll.tabs.overtime),
            monthly: t(payroll.tabs.monthly),
        }),
        [t, payroll.tabs],
    )

    const filteredTabTranslations = useMemo(
        () =>
            tabs.reduce(
                (acc, tab) => {
                    acc[tab] =
                        tabTranslations[tab as keyof typeof tabTranslations]
                    return acc
                },
                {} as Record<string, string>,
            ),
        [tabs, tabTranslations],
    )

    const currentPath = locationFromRouter.pathname.split('/').pop() || tabs[0]
    const activeTab = tabs.includes(currentPath) ? currentPath : tabs[0]

    const handleTabSelect = (activeTab: string) => {
        logClick('payroll tab ' + activeTab)
        const englishTabName = Object.keys(filteredTabTranslations).find(
            (key) => filteredTabTranslations[key] === activeTab,
        )
        const path =
            tabToPath[(englishTabName as keyof typeof tabToPath) || tabs[0]]
        const fullPath = `/payroll/${path}${locationFromRouter.search}`
        if (locationFromRouter.pathname !== fullPath) {
            void navigate(fullPath)
        }
    }

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={t(payroll.title)}
                />
            )}
        >
            <MPMLayout
                heading={t(payroll.title)}
                className="hc8-ph-none payroll-page"
                hasTabs
                tabs={
                    <PageTabs
                        activeTab={filteredTabTranslations[activeTab]}
                        className="tabs hc8-pv-2x"
                        disableAdaptive={true}
                        onTabSelect={handleTabSelect}
                    >
                        {Object.keys(filteredTabTranslations).map((tab) => (
                            <Tabs.Item
                                key={tab}
                                name={filteredTabTranslations[tab]}
                            >
                                {filteredTabTranslations[tab]}
                            </Tabs.Item>
                        ))}
                    </PageTabs>
                }
            >
                <div className="hc8-ph-4x">
                    <Outlet />
                </div>
            </MPMLayout>
        </ErrorBoundary>
    )
}
